import {
  cloneDeepWith,
  difference,
  isObject,
  mergeWith,
  isBoolean,
  isEqual,
  findIndex,
  get,
} from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import isUUID from 'validator/es/lib/isUUID';
import { orderByColumns } from '@/constants/fields';
import { defaultLifeCycle } from '@/constants/lifecycle';
import errorCodes from '@/constants/errors';
import { formatDate } from './dateHelper';

export const findMatch = (items, code, attribute = 'code') => {
  const match = code || null;
  return items.find((r) => (get(r, attribute) === match));
};

export const findResultInCategory = (items, code, attribute = 'code') => (
  items?.results ? findMatch(items.results, code, attribute) : null
);

const findResult = (items, category, code, attribute = 'code') => (
  items?.[category] ? findResultInCategory(items[category], code, attribute) : null
);

export const findMatches = (items, code, attribute = 'code') => {
  const match = code || null;
  return items.filter((r) => (get(r, attribute) === match));
};

export const findResultsInCategory = (items, code, attribute = 'code') => (
  items?.results ? findMatches(items.results, code, attribute) : null
);

export const findResults = (items, category, code, attribute = 'code') => (
  items?.[category] ? findResultsInCategory(items[category], code, attribute) : []
);

export const isPlainObject = (item) => (
  typeof item === 'object' && !Array.isArray(item) && item !== null && !(item instanceof Date)
);

export const hasPlainObject = (item) => (Array.isArray(item) && item.some(isPlainObject));

export const countSelected = (items, selected = true) => items.reduce((n, x) => n + (x.selected === selected), 0);

export const allSelected = (items, selected = true) => (
  items.length > 0 ? items.length === countSelected(items, selected) : false
);

export const countByAttribute = (items, attribute, value) => items.reduce((n, x) => n + (x[attribute] === value), 0);

export const countSelectedInCategory = (items, category, selected = true) => (
  countSelected(items[category].results, selected)
);

export const firstSelected = (items, selected = true) => items.find((r) => (r.selected === selected));

export const findSelected = (items, selected = true) => items.filter((r) => (r.selected === selected));

export const allOrSelected = (items) => {
  const selected = findSelected(items);
  return selected.length > 0 ? selected : items;
};

export const findSelectedInCategory = (items, category, selected = true) => (
  findSelected(items[category].results, selected)
);

export const findWithReadStatus = (items, categories, status) => (
  (items) ? categories.filter((category) => items[category].readStatus === status) : []
);

export const findWithCode = (items, categories, code, attribute = 'code') => categories.filter(
  (category) => findResult(items, category, code, attribute),
);

export const findWithCodes = (items, categories, codes, attribute = 'code') => {
  const matched = codes.map((code) => findWithCode(items, categories, code, attribute));
  return [...new Set([].concat(...matched))];
};

export const successfulDestroy = (result, message) => !result.error
  || (result.error.code === errorCodes.notFound && result.error.statusText
    && result.error.statusText.toLowerCase() === message.toLowerCase());

export const successfulAssociation = (result, message) => successfulDestroy(result, message);

export const successAndErrorsOnDestroy = (results, successfulDestroyMessage) => {
  const [success, errors] = results.reduce((result, element) => {
    result[successfulDestroy(element, successfulDestroyMessage) ? 0 : 1].push(element);
    return result;
  }, [[], []]);

  return { success, errors };
};

export const successAndErrors = (results) => {
  const [success, errors] = results.reduce((result, element) => {
    result[element.error ? 1 : 0].push(element);
    return result;
  }, [[], []]);

  return { success, errors };
};

export const successAndErroredResponses = (destroyed, created, updated, successfulDestroyMessage) => {
  const destroyedItems = successAndErrorsOnDestroy(destroyed, successfulDestroyMessage);
  const createdItems = successAndErrors(created);
  const updatedItems = successAndErrors(updated);

  return {
    success: destroyedItems.success.concat(createdItems.success).concat(updatedItems.success),
    errors: destroyedItems.errors.concat(createdItems.errors).concat(updatedItems.errors),
  };
};

export const safeArray = (value) => (Array.isArray(value) ? value : [value]);

export const stringToCheck = (value, defaultValue = '') => (
  (String(value === undefined || value === null ? defaultValue : value)).trim()
);

export const stringsToCheck = (value, defaultValue = '') => safeArray(value).map((v) => stringToCheck(v, defaultValue));

export const hasMinimumLength = (value, minLength = 1, trimChars = []) => {
  let trimmedValue = stringToCheck(value);
  const chars = trimChars.filter((char) => trimmedValue.includes(char));
  chars.forEach((str) => {
    trimmedValue = trimmedValue.replaceAll(str, '');
  });

  return trimmedValue.trim().length >= minLength;
};

export const isEmpty = (item) => (item === undefined || item === null);

export const removeEmpty = (items) => items.filter((item) => !isEmpty(item));

const isUnique = (item, seen, caseInsensitive = false) => {
  const value = caseInsensitive && hasMinimumLength(item) ? item.toString().toLowerCase() : item;
  if (seen.has(value)) return false;

  seen.add(value);
  return hasMinimumLength(item);
};

export const removeDuplicates = (items, caseInsensitive = false) => {
  const seen = new Set();
  return items.filter((item) => isUnique(item, seen, caseInsensitive));
};

export const flattenObjectsArray = (arr, parentKey = 'item') => arr.map((obj) => {
  const result = {};
  const recurse = (current, keyPrefix) => {
    Object.entries(current).forEach(([key, value]) => {
      const newKey = keyPrefix ? `${keyPrefix}.${key}` : key;
      if (isPlainObject(value)) {
        recurse(value, newKey);
      } else if (hasPlainObject(value)) {
        value.forEach((item) => recurse(item, newKey));
      } else {
        const values = Array.isArray(value) ? value : [value];
        result[newKey] = stringsToCheck(removeDuplicates(values, true)).map(((v) => v.toLowerCase())).sort();
      }
    });
  };

  recurse(obj, parentKey);
  return result;
});

export const findResultByEvent = (items, { category, code }, event) => {
  const item = findResult(items, category, code);
  return item ? findMatch([item], event, 'event') : null;
};

export const removeItemByCodeAndEvent = (results, { code, event }) => (
  results.filter((r) => r.code !== code && r.event !== event)
);

export const mapResults = (existing, { results }, fn) => existing.concat(results.map(fn));

// eslint-disable-next-line no-param-reassign
export const assignValue = (item, key, value) => { item[key] = value; };

// eslint-disable-next-line consistent-return
const replaceIfArray = ((objValue, srcValue) => {
  if (Array.isArray(objValue)) {
    return srcValue;
  }
});

export const jsonTryParse = (json, defaultIfFails) => {
  try {
    return JSON.parse(json);
  } catch (e) {
    return defaultIfFails;
  }
};

export const capitaliseString = (value, defaultValue = '') => {
  const text = stringToCheck(value, defaultValue);
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const newName = (prefix, names) => {
  const lowered = names.map((n) => stringToCheck(n).toLowerCase());
  if (!lowered.includes(prefix.toLowerCase())) {
    return prefix;
  }

  let nextName = '';
  let index = names.length;
  do {
    index += 1;
    nextName = `${stringToCheck(prefix)} (${index})`;
  } while (lowered.includes(nextName.toLowerCase()));

  return nextName;
};

export const newNames = (prefix, names, count) => {
  const items = [];

  if (count > 0) {
    const copy = [...names];
    do {
      const name = newName(prefix, copy);
      items.push(name);
      copy.push(name);
    } while (items.length !== count);
  }

  return items;
};

export const hasMinimumLengths = (values, minLength = 1, trimChars = []) => (
  values.every((value) => hasMinimumLength(value, minLength, trimChars))
);

export const filterWithMinimumLength = (items, minLength = 1, trimChars = []) => (
  items.filter((item) => hasMinimumLength(item, minLength, trimChars))
);

export const isValidFilter = (value, { requiresFilter, minFilterLength }) => (
  !requiresFilter || hasMinimumLength(value, minFilterLength, ['*'])
);

export const wildcardFilter = (filter) => (hasMinimumLength(filter) ? `*${filter}*` : null);

export const hasKey = (item, key) => Object.prototype.hasOwnProperty.call(item, key);

export const createOrderByParams = (orderByMap = []) => {
  if (!orderByMap || orderByMap.length === 0) {
    return '';
  }

  return orderByMap.map((item) => `${orderByColumns[item.column] || item.column} ${item.direction}`).join(',');
};

export const nextOrderBy = (currentOrderBy, canBeClearedResult = true) => {
  if (!currentOrderBy) {
    return 'asc';
  }

  const nextValuesIfCanBeCleared = { asc: 'desc', desc: null };
  const nextValuesIfCanNotBeCleared = { asc: 'desc', desc: 'asc' };
  return canBeClearedResult ? nextValuesIfCanBeCleared[currentOrderBy] : nextValuesIfCanNotBeCleared[currentOrderBy];
};

const findOrderByItemIndex = (orderBy, column) => findIndex(orderBy, (item) => item.column === column);

export const findOrderByItem = (orderBy, column) => orderBy.find((item) => item.column === column);

export const canBeCleared = (orderBy, singleSorting) => !(singleSorting || orderBy.length <= 1);

export const mapItemOrderByValues = (orderBy, orderByColumn, singleSorting) => {
  const orderByItem = findOrderByItem(orderBy, orderByColumn);
  const orderByItemIndex = orderByItem ? findOrderByItemIndex(orderBy, orderByColumn) : orderBy.length;
  const newOrderBy = singleSorting ? [] : [...orderBy.filter((item) => item.column !== orderByColumn)];
  const canBeClearedResult = canBeCleared(orderBy, singleSorting);
  const newDirection = nextOrderBy(orderByItem ? orderByItem.direction : null, canBeClearedResult);
  if (newDirection) {
    const newOrderByItem = { column: orderByColumn, direction: newDirection };
    newOrderBy.splice(orderByItemIndex, 0, newOrderByItem);
  }

  return newOrderBy;
};

export const replaceEmptyString = (value) => ((isObject(value)
  || isBoolean(value)
  || hasMinimumLength(value)) ? undefined : null);

export const replaceEmptyStrings = (object) => cloneDeepWith(object, replaceEmptyString);

export const resultDiff = (current, previous) => {
  const result = {};

  if (!isEqual(current, previous)) {
    Object.keys(current).forEach((field) => {
      if (!isEqual(current[field], previous[field])) {
        result[field] = current[field];
      }
    });
  }

  return Object.keys(result).length > 0 ? result : null;
};

const assignProperty = (
  item,
  key,
  props,
  lifecycleProps = defaultLifeCycle,
  hasProp = false,
) => {
  if (hasProp || hasKey(item, key)) {
    const isDirty = item[key] !== props[key] && !lifecycleProps.includes(key);
    if (isPlainObject(props[key]) && item[key]) {
      mergeWith(item[key], props[key], replaceIfArray);
    } else {
      assignValue(item, key, props[key]);
    }
    return isDirty;
  }

  return false;
};

const checkIsDirty = (item, markDirty, changes) => {
  const isDirty = changes.includes(true);
  if (isDirty && markDirty) {
    assignValue(item, 'isDirty', true);
  }
  return isDirty;
};

export const assignProps = (
  item,
  props,
  lifecycleProps = [],
  markDirty = false,
) => checkIsDirty(item, markDirty, Object.keys(props).map((key) => (
  assignProperty(item, key, props, lifecycleProps, true)
)));

export const assignIfExists = (
  item,
  props,
  lifecycleProps = defaultLifeCycle,
  markDirty = false,
) => checkIsDirty(item, markDirty, Object.keys(props).map((key) => (
  assignProperty(item, key, props, lifecycleProps)
)));

export const assignIfExistsToMany = (
  items,
  props,
  lifecycleProps = defaultLifeCycle,
  markDirty = false,
) => items.map((item) => assignIfExists(item, props, lifecycleProps, markDirty)).includes(true);

export const removeItemByCode = (results, { code }) => results.filter((r) => r.code !== code);

export const removeItemByID = (results, { id }) => results.filter((r) => r.id.toString() !== id.toString());

export const findItemByID = (results, { id }) => results.find((r) => r.id.toString() === id.toString());

export const findItemByMemberID = (members, memberID) => members.find(
  (member) => (member.uuid && member.uuid === memberID) || (member.code && member.code === memberID),
);

export const removeItemByMemberID = (members, memberID) => members.filter(
  (member) => (member.uuid && member.uuid !== memberID) || (member.code && member.code !== memberID),
);

export const removeItemArr = (arr, item) => arr.filter((f) => f !== item);

export const removeItemsArr = (arr, items) => difference(arr, items);

export const addUniqueItemByCode = (results, item) => {
  const exists = results.find((r) => r.code === item.code);
  if (exists) {
    return null;
  }
  results.push(item);
  return results;
};

export const indexOfKey = (items, key) => (Object.keys(items).sort().findIndex((k) => k === key));

const nextKey = (items, index) => {
  const keys = Object.keys(items).sort();
  let [key] = keys;

  if (index < 0) {
    key = keys[keys.length - 1];
  } else if (index >= items.length) {
    key = items[0].uuid;
  } else if (index < keys.length && index >= 0) {
    key = keys[index];
  }

  return key;
};

export const previousCarouselKey = (items, key) => nextKey(items, indexOfKey(items, key) - 1);

export const nextCarouselKey = (items, key) => nextKey(items, indexOfKey(items, key) + 1);

export const formatPhoneNumber = (phoneNumber) => (hasMinimumLength(phoneNumber) ? `+${phoneNumber}` : '');

export const isChecked = (value) => (['t', 'true'].includes((stringToCheck(value, 'false')).toLowerCase()));

export const formatBoolean = (value, intl) => (
  intl.formatMessage({ id: isChecked(value) ? 'boolean.true' : 'boolean.false' })
);

const uuidLiteral = (value, defaultValue = null) => {
  const lowered = (String(value || '')).trim().toLowerCase();
  if (lowered === 'v4') { return uuidv4(); }
  return defaultValue;
};

export const parseUuid = (text) => (text && isUUID(text) ? text : uuidLiteral(text));

export const formatList = (value, options) => {
  const item = (options || []).find((option) => option.value === value);
  return item ? item.text : value;
};

export const selectableResult = (result) => ({ ...result, selected: false });

export const resultsWithDates = (result) => ({
  ...result,
  createdOn: result.created_at ? formatDate(result.created_at) : '',
  updatedOn: result.updated_at ? formatDate(result.updated_at) : '',
});

export const addSelectedToResults = (results) => results.map(selectableResult);

export const selectableResultWithDates = (result) => ({
  ...selectableResult(result),
  ...resultsWithDates(result),
});

const nextItemIndex = (items, nextIndex, backwards = false) => {
  const validIndexes = removeEmpty(items.map((i, index) => (i ? index : null)))
    .filter((i) => (backwards ? i <= nextIndex : i >= nextIndex));
  const orderedIndexes = backwards ? validIndexes.reverse() : validIndexes;

  return orderedIndexes[0];
};

export const previousCarouselIndex = (items, index) => {
  let next = nextItemIndex(items, index - 1, true);
  if (next === undefined) {
    next = nextItemIndex(items, items.length, true);
  }

  return next === undefined ? 0 : next;
};

export const nextCarouselIndex = (items, index) => {
  let next = nextItemIndex(items, index + 1);
  if (next === undefined) {
    next = nextItemIndex(items, 0);
  }

  return next === undefined ? 0 : next;
};

const nextItem = (items, index, attribute = 'code') => {
  let value = '';
  if (index < 0) {
    value = items[items.length - 1][attribute];
  } else if (index >= items.length) {
    value = items[0][attribute];
  } else if (index < items.length && index >= 0) {
    value = items[index][attribute];
  }

  return value;
};

export const indexOfItem = (items, code, attribute = 'code') => (
  items.findIndex((item) => (item[attribute] === code))
);

export const previousCarouselitem = (items, code, attribute = 'code') => (
  nextItem(items, indexOfItem(items, code, attribute) - 1, attribute)
);

export const nextCarouselItem = (items, code, attribute = 'code') => (
  nextItem(items, indexOfItem(items, code, attribute) + 1, attribute)
);

export const uniqueByCode = (items) => items.filter((v, i, a) => a.findIndex((t) => (t.code === v.code)) === i);

export const propertiesWithValue = ({ name }, value) => {
  const properties = {};
  properties[name] = value;

  return properties;
};

export const addOrReplace = (arr, newObj, attribute = 'code') => (
  [...arr.filter((o) => o[attribute] !== newObj[attribute]), { ...newObj }]
);

export const compareByAttributes = (itemA, itemB, path, valueIfNotPresent = '') => {
  let result = 0;
  const valueA = get(itemA, path, valueIfNotPresent);
  const valueB = get(itemB, path, valueIfNotPresent);

  if (valueA < valueB) {
    result = -1;
  } else if (valueA > valueB) {
    result = 1;
  }

  return result;
};

export const compareByName = (itemA, itemB) => compareByAttributes(itemA, itemB, 'name');
export const compareByText = (itemA, itemB) => compareByAttributes(itemA, itemB, 'text');
export const compareByTitle = (itemA, itemB) => compareByAttributes(itemA, itemB, 'title');
export const compareByUrl = (itemA, itemB) => compareByAttributes(itemA, itemB, 'url');

export const commonNameAttributes = ['name', 'title', 'code', 'uuid', 'id'];

export const identifiableName = (items, identifiers = commonNameAttributes) => (
  filterWithMinimumLength(identifiers.map((key) => get(items[0], key)))[0]
);

export const truncateText = (text, len = 8) => ((text.length > len) ? `${text.substring(0, len - 2)}..` : text);

export default findResult;
